import { createSwiper } from '@nulo/swiper';

/**
 * @extends {HTMLElement}
 */
class OurNutritionists extends HTMLElement {
  /** @type {import('swiper').Swiper | null} */
  #swiper = null;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    if (this.#abortController) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#initSwiper();
    this.#bindInfoToggles(signal);
  }

  disconnectedCallback() {
    this.#swiper?.destroy?.(true, true);
    this.#swiper = null;

    this.#abortController?.abort();
    this.#abortController = null;
  }

  #initSwiper() {
    const swiperEl = this.querySelector('[data-swiper]');
    if (!(swiperEl instanceof HTMLElement)) return;

    const slideCount = swiperEl.querySelectorAll('.swiper-slide').length;
    if (slideCount <= 1) return;

    this.#swiper = createSwiper({
      region: this,
      el: swiperEl,
      activeSlideClass: 'swiper-slide-fully-visible',
      config: {
        slidesPerView: 1,
        slidesPerGroup: 1,
        speed: 500,
        watchSlidesProgress: true,
        navigation: {
          nextEl: this.querySelector('.our-nutritionists__nav--next'),
          prevEl: this.querySelector('.our-nutritionists__nav--prev'),
        },
        pagination: {
          el: this.querySelector('[data-nutritionists-pagination]'),
          clickable: true,
        },
      },
    });
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindInfoToggles(signal) {
    for (const media of this.querySelectorAll('[data-nutritionist-media]')) {
      if (!(media instanceof HTMLElement)) continue;

      const openBtn = media.querySelector('[data-nutritionist-info-open]');
      const closeBtn = media.querySelector('[data-nutritionist-info-close]');

      openBtn?.addEventListener(
        'click',
        (event) => {
          event.stopPropagation();
          this.#setOverlayOpen(media, true);
        },
        { signal }
      );

      closeBtn?.addEventListener(
        'click',
        (event) => {
          event.stopPropagation();
          this.#setOverlayOpen(media, false);
        },
        { signal }
      );

      media.addEventListener(
        'focus',
        () => {
          if (window.matchMedia('(min-width: 768px)').matches) return;
          this.#setOverlayOpen(media, true);
        },
        { signal }
      );

      media.addEventListener(
        'blur',
        (event) => {
          if (window.matchMedia('(min-width: 768px)').matches) return;
          const related = event.relatedTarget;
          if (related instanceof Node && media.contains(related)) return;
          this.#setOverlayOpen(media, false);
        },
        { signal }
      );
    }
  }

  /**
   * @param {HTMLElement} media
   * @param {boolean} isOpen
   */
  #setOverlayOpen(media, isOpen) {
    media.classList.toggle('is-overlay-open', isOpen);

    const openBtn = media.querySelector('[data-nutritionist-info-open]');
    const closeBtn = media.querySelector('[data-nutritionist-info-close]');

    if (openBtn instanceof HTMLButtonElement) {
      openBtn.hidden = isOpen;
    }

    if (closeBtn instanceof HTMLButtonElement) {
      closeBtn.hidden = !isOpen;
    }
  }
}

if (!customElements.get('our-nutritionists')) {
  customElements.define('our-nutritionists', OurNutritionists);
}
